/**
 * /relationships/:characterId — full detail for one character relationship.
 * Reads GET /api/fan/relationships/{id}. Renders:
 *   - Big level badge + animated progress bar
 *   - First-person memory card (or graceful empty state)
 *   - Milestones timeline (newest first)
 *   - "繼續聊天" CTA back into /c/:id/chat
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getMyRelationship,
  respondToDailyGreet,
  postMoodCheckin,
  type AffinityLevelName,
  type RelationshipDetail,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const MOOD_OPTIONS: ReadonlyArray<{ emoji: string; label: string }> = [
  { emoji: '😊', label: '開心' },
  { emoji: '🤗', label: '感激' },
  { emoji: '🤩', label: '興奮' },
  { emoji: '😎', label: '從容' },
  { emoji: '🤔', label: '困惑' },
  { emoji: '😴', label: '累了' },
  { emoji: '😢', label: '難過' },
  { emoji: '😠', label: '生氣' },
];

const LEVEL_TONE: Record<AffinityLevelName, { bg: string; text: string; bar: string; border: string }> = {
  '陌生':       { bg: 'bg-slate-800',      text: 'text-slate-300',   bar: 'bg-slate-500',    border: 'border-slate-700' },
  '點頭之交':   { bg: 'bg-sky-500/20',     text: 'text-sky-300',     bar: 'bg-sky-500',      border: 'border-sky-500/40' },
  '朋友':       { bg: 'bg-blue-500/20', text: 'text-blue-300', bar: 'bg-blue-500',  border: 'border-blue-500/40' },
  '親近':       { bg: 'bg-amber-500/20',   text: 'text-amber-300',   bar: 'bg-amber-500',    border: 'border-amber-500/40' },
  '摯友':       { bg: 'bg-rose-500/20',    text: 'text-rose-300',    bar: 'bg-rose-500',     border: 'border-rose-500/40' },
  '靈魂相依':   { bg: 'bg-violet-500/20',  text: 'text-violet-300',  bar: 'bg-violet-500',   border: 'border-violet-500/40' },
};

function progressWithinLevel(detail: RelationshipDetail): number {
  const [lo, hi] = detail.valueRangeOfCurrentLevel;
  const span = Math.max(1, hi - lo);
  return Math.max(0, Math.min(1, (detail.value - lo) / span));
}

export function RelationshipDetailPage() {
  const { characterId } = useParams<{ characterId: string }>();
  useDocumentTitle('角色關係');
  const [detail, setDetail] = useState<RelationshipDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [greetResult, setGreetResult] = useState<{ leveledUp: boolean; newLevelName: AffinityLevelName } | null>(null);
  const [moodEmoji, setMoodEmoji] = useState<string | null>(null);
  const [moodNote, setMoodNote] = useState('');
  const [moodPhase, setMoodPhase] = useState<'idle' | 'submitting' | 'done'>('idle');

  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    void getMyRelationship(characterId)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [characterId]);

  if (!characterId) return null;

  if (err) {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-4 text-center">
          <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-300 text-3xl">✕</div>
          <h1 className="text-xl font-semibold">無法載入</h1>
          <p className="text-slate-400 text-sm break-words">{err}</p>
          <Link to="/relationships" className="block w-full rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition">回養成列表</Link>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-12 text-center">
        <div className="inline-block w-10 h-10 border-[3px] border-slate-700 border-t-indigo-400 rounded-full animate-spin" aria-label="載入中" />
      </div>
    );
  }

  const tone = LEVEL_TONE[detail.levelName];
  const pct = progressWithinLevel(detail);
  const [lo, hi] = detail.valueRangeOfCurrentLevel;

  return (
    <div className="max-w-md mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      {/* Hero — level badge + score + streak. */}
      <section className={`rounded-2xl ${tone.bg} ${tone.border} border p-5 space-y-3 text-center`}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-300">{detail.characterName}</div>
        <div className={`text-5xl font-bold ${tone.text}`} aria-label={`等級 ${detail.level}`}>
          Lv {detail.level}
        </div>
        <div className={`text-base font-semibold ${tone.text}`}>{detail.levelName}</div>
        <div className="h-2.5 w-full bg-slate-950/50 rounded-full overflow-hidden">
          <div
            className={`h-full ${tone.bar} transition-[width] duration-700`}
            style={{ width: `${Math.round(pct * 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-slate-300 tabular-nums">
          <span>{lo}</span>
          <span className="font-semibold">{detail.value} / 100</span>
          <span>{hi}</span>
        </div>
        {detail.valueToNextLevel !== null ? (
          <p className="text-xs text-slate-400">
            還差 <span className="font-semibold text-slate-200">{detail.valueToNextLevel}</span> 點到下一級
          </p>
        ) : (
          <p className="text-xs text-violet-300">已達最高等級 ✨</p>
        )}
        {detail.streakDays > 0 && (
          <p className="text-xs text-amber-300">🔥 連續 {detail.streakDays} 天互動</p>
        )}
      </section>

      {/* Daily greeting card. When today is a known holiday (元旦 /
          情人節 / 春節 / 中秋 / 聖誕 / 跨年 …) the card themes
          gold + holiday emoji + bigger +3 reward. Otherwise regular
          amber +2 daily-greet. Tap "回應" credits affinity once per
          UTC day. */}
      {(detail.dailyGreetAvailable || greeting === 'done') && detail.dailyGreetMessage && (() => {
        const isHoliday = !!detail.dailyGreetHolidayId;
        const sectionTone = isHoliday
          ? 'border-yellow-400/50 bg-gradient-to-br from-yellow-400/20 to-orange-500/10'
          : 'border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-amber-500/5';
        const eyebrowTone = isHoliday ? 'text-yellow-300' : 'text-amber-300';
        const bodyTone = isHoliday ? 'text-yellow-50' : 'text-amber-100';
        const ctaTone = isHoliday
          ? 'bg-yellow-400 hover:bg-yellow-300 text-slate-900'
          : 'bg-amber-500 hover:bg-amber-400 text-slate-900';
        const headerIcon = detail.dailyGreetHolidayEmoji ?? '💌';
        const reward = detail.dailyGreetReward || 2;
        const eyebrow = isHoliday
          ? `${detail.dailyGreetHolidayName} · 今日問候`
          : '今日的問候';
        return (
          <section className={`rounded-2xl border ${sectionTone} p-4 sm:p-5 space-y-3`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0" aria-hidden="true">{headerIcon}</span>
              <div className="flex-1 min-w-0">
                <div className={`text-[10px] uppercase tracking-[0.2em] ${eyebrowTone}`}>{eyebrow}</div>
                <p className={`text-sm ${bodyTone} mt-1 leading-relaxed`}>
                  {detail.dailyGreetMessage}
                </p>
                {detail.personalizedGreetingHint && (
                  <p className="text-[11px] text-slate-400 mt-1.5 italic">
                    {detail.personalizedGreetingHint}
                  </p>
                )}
              </div>
            </div>
            {greeting === 'done' ? (
              <div className="rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-200 text-sm font-medium py-2 px-3 text-center">
                ✓ 已回應 · +{reward} 好感度
                {greetResult?.leveledUp && (
                  <span className="ml-2 text-violet-300">🎊 升到「{greetResult.newLevelName}」！</span>
                )}
              </div>
            ) : (
              <button
                type="button"
                disabled={greeting === 'submitting'}
                onClick={async () => {
                  setGreeting('submitting');
                  try {
                    const r = await respondToDailyGreet(detail.characterId);
                    setGreetResult({ leveledUp: r.leveledUp, newLevelName: r.newLevelName });
                    // Optimistically update local state so the bar moves.
                    setDetail((d) => d ? { ...d, value: r.newValue, level: r.newLevel, levelName: r.newLevelName, dailyGreetAvailable: false } : d);
                    setGreeting('done');
                  } catch (e) {
                    setErr((e as Error).message);
                    setGreeting('idle');
                  }
                }}
                className={`w-full rounded-lg ${ctaTone} disabled:bg-slate-700 disabled:text-slate-500 font-semibold py-3 transition active:scale-[0.99]`}
              >
                {greeting === 'submitting' ? '回應中…' : `回應 (+${reward} 好感度)`}
              </button>
            )}
          </section>
        );
      })()}

      {/* Mood check-in card. Once per UTC day, +1 affinity. After
          submit, the card stays visible in read-only form showing
          the chosen emoji + note. */}
      {detail.moodCheckinAvailable ? (
        <section className="rounded-2xl border border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-pink-500/5 p-4 sm:p-5 space-y-3">
          <div className="flex items-start gap-2">
            <span className="text-2xl" aria-hidden="true">📝</span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300">今天的心情</div>
              <p className="text-xs text-slate-400 mt-1">挑一個心情、再寫一句話，{detail.characterName} 會默默記住。</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {MOOD_OPTIONS.map((m) => (
              <button
                key={m.emoji}
                type="button"
                onClick={() => setMoodEmoji(m.emoji)}
                aria-label={`選擇心情：${m.label}`}
                className={`rounded-xl border py-3 transition active:scale-95 ${
                  moodEmoji === m.emoji
                    ? 'border-rose-400 bg-rose-500/20 ring-2 ring-rose-400'
                    : 'border-slate-700 bg-slate-900/40 hover:border-slate-600'
                }`}
              >
                <div className="text-2xl">{m.emoji}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{m.label}</div>
              </button>
            ))}
          </div>
          <input
            type="text"
            value={moodNote}
            onChange={(e) => setMoodNote(e.target.value)}
            maxLength={160}
            placeholder="想說一句話嗎？（選填）"
            className="w-full rounded-lg bg-slate-950 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <button
            type="button"
            disabled={!moodEmoji || moodPhase === 'submitting'}
            onClick={async () => {
              if (!moodEmoji) return;
              setMoodPhase('submitting');
              try {
                const r = await postMoodCheckin(detail.characterId, {
                  emoji: moodEmoji,
                  ...(moodNote.trim() ? { note: moodNote.trim() } : {}),
                });
                setDetail((d) => d ? {
                  ...d, value: r.newValue, level: r.newLevel, levelName: r.newLevelName,
                  moodCheckinAvailable: false, todayMoodEmoji: r.emoji, todayMoodNote: r.note,
                } : d);
                setMoodPhase('done');
              } catch (e) {
                setErr((e as Error).message);
                setMoodPhase('idle');
              }
            }}
            className="w-full rounded-lg bg-rose-500 hover:bg-rose-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold py-3 transition active:scale-[0.99]"
          >
            {moodPhase === 'submitting' ? '紀錄中…' : '記錄今天的心情 (+1)'}
          </button>
        </section>
      ) : detail.todayMoodEmoji && (
        <section className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300">今天你選了</div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-4xl" aria-hidden="true">{detail.todayMoodEmoji}</span>
            {detail.todayMoodNote && (
              <p className="text-sm text-slate-200 italic leading-relaxed flex-1 min-w-0">「{detail.todayMoodNote}」</p>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">明天可以再來打卡 ✨</p>
        </section>
      )}

      {/* First-person memory card. */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 sm:p-5 space-y-2">
        <div className="text-[11px] uppercase tracking-[0.2em] text-rose-400">角色對你的印象</div>
        {detail.memoryCard ? (
          <>
            <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed border-l-2 border-rose-500/40 pl-3 italic">
              {detail.memoryCard}
            </p>
            {detail.memoryCardUpdatedAt && (
              <p className="text-[10px] text-slate-500">
                {new Date(detail.memoryCardUpdatedAt).toLocaleString()} 更新
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500 italic">
            角色的印象卡會在你們互動累積到一定程度後自動產生。先多聊幾次吧。
          </p>
        )}
      </section>

      {/* Milestones timeline. */}
      {detail.milestones.length > 0 && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-violet-400">里程碑</div>
            <Link
              to={`/relationships/${encodeURIComponent(detail.characterId)}/album`}
              className="text-xs text-violet-300 hover:text-violet-200"
              aria-label="開啟回憶相簿"
            >
              翻開相簿 →
            </Link>
          </div>
          <ul className="space-y-3">
            {[...detail.milestones].reverse().map((m, i) => {
              const t = LEVEL_TONE[m.levelName];
              return (
                <li key={`${m.crossedAt}-${i}`} className="flex gap-3">
                  <div className={`${t.bg} ${t.text} h-9 w-9 rounded-full flex flex-col items-center justify-center shrink-0`}>
                    <span className="text-[8px] leading-none">Lv</span>
                    <span className="text-xs font-bold leading-none mt-0.5">{m.level}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${t.text}`}>升到「{m.levelName}」</div>
                    <div className="text-[10px] text-slate-500 tabular-nums">{new Date(m.crossedAt).toLocaleString()}</div>
                    {m.context && (
                      <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">「{m.context}」</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Level threshold unlocks — each completed level (1-5) reveals
          a quote + conversation starter + sticker. Newest level
          first so the latest unlock is most visible. Empty when fan
          is still at level 0. */}
      {detail.unlocks.length > 0 && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 sm:p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400">解鎖的回憶</div>
          <ul className="space-y-2">
            {[...detail.unlocks].reverse().map((u) => (
              <li
                key={u.level}
                className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-2"
              >
                <div className="flex items-center gap-3">
                  {/* Operator-uploaded sticker image takes precedence
                      over the default emoji. */}
                  {u.stickerUrl ? (
                    <img
                      src={u.stickerUrl}
                      alt={`Lv ${u.level} 紀念貼圖`}
                      className="h-12 w-12 rounded-lg object-cover border border-slate-700 shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-2xl shrink-0" aria-hidden="true">{u.stickerEmoji}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500">Lv {u.level} · {u.levelName}</div>
                    <p className="text-sm text-slate-200 italic leading-relaxed">
                      「{u.characterQuote}」
                    </p>
                  </div>
                </div>
                {/* Operator-uploaded voice clip — render an inline
                    HTML5 audio player when set. */}
                {u.voiceUrl && (
                  <audio
                    controls
                    src={u.voiceUrl}
                    className="w-full h-9"
                    preload="none"
                    aria-label={`Lv ${u.level} 紀念語音`}
                  />
                )}
                {/* Conversation-starter chip — taps into the chat
                    page with a prefilled (TBD: future iter) seed
                    via query string. For now just a styled link. */}
                <Link
                  to={`/c/${encodeURIComponent(detail.characterId)}/chat`}
                  className="block rounded-lg bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/30 text-violet-200 text-sm py-2 px-3 transition text-center"
                  aria-label={`用「${u.conversationStarter}」開啟對話`}
                >
                  💬 {u.conversationStarter}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Mini-app launchers + chat CTA. */}
      <div className="grid grid-cols-2 gap-2">
        <Link
          to={`/pomodoro/${encodeURIComponent(detail.characterId)}`}
          className="rounded-2xl border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 text-rose-200 font-medium py-3 px-3 text-center active:scale-[0.99] transition"
          aria-label="開始番茄鐘陪讀"
        >
          <div className="text-2xl" aria-hidden="true">🍅</div>
          <div className="text-xs mt-1">番茄鐘陪讀</div>
          <div className="text-[10px] text-rose-300/70">+5 好感度</div>
        </Link>
        <div
          aria-disabled="true"
          title={detail.moodCheckinAvailable ? '往上滑就能打卡 ✨' : '今天已經打過卡'}
          className={`rounded-2xl border py-3 px-3 text-center ${
            detail.moodCheckinAvailable
              ? 'border-rose-500/30 bg-rose-500/5 text-rose-300'
              : 'border-blue-500/30 bg-blue-500/5 text-blue-300'
          }`}
        >
          <div className="text-2xl" aria-hidden="true">📝</div>
          <div className="text-xs mt-1">心情打卡</div>
          <div className="text-[10px] opacity-70">
            {detail.moodCheckinAvailable ? '可打卡 +1' : '✓ 今日已打卡'}
          </div>
        </div>
      </div>
      <Link
        to={`/c/${encodeURIComponent(detail.characterId)}/chat`}
        className="block w-full rounded-2xl bg-gradient-to-r from-violet-500 to-violet-400 hover:from-violet-400 hover:to-violet-300 text-white font-medium py-4 text-center shadow-lg shadow-violet-500/20 active:scale-[0.99] transition"
      >
        <span className="text-2xl mr-2" aria-hidden="true">💬</span>
        繼續跟「{detail.characterName}」聊天
      </Link>
    </div>
  );
}
